import React, { useState, useEffect } from 'react';
import './ResendEmailVerification.css';

const ResendEmailVerification: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsEmailSent(true);
      setCountdown(60); // 60 second cooldown
    } catch (err) {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/';
  };

  if (isEmailSent) {
    return (
      <div className="resend-email-container">
        <div className="resend-email-card">
          <div className="email-sent-icon">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="#3b82f6"/>
              <path d="M25 35L40 45L55 35M25 35L40 25L55 35M25 35V55C25 57.2091 26.7909 59 29 59H51C53.2091 59 55 57.2091 55 55V35" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h1 className="success-title">Verification Email Sent!</h1>
          
          <p className="success-message">
            We've sent a new verification email to <strong>{email}</strong>. 
            Please check your inbox and click the verification link.
          </p>
          
          <div className="email-tips">
            <h3>Don't see the email?</h3>
            <ul>
              <li>Check your spam or junk folder</li>
              <li>Make sure you entered the correct email address</li>
              <li>Wait a few minutes for the email to arrive</li>
            </ul>
          </div>
          
          <div className="action-buttons">
            {countdown > 0 ? (
              <button className="resend-button disabled" disabled>
                Resend in {countdown}s
              </button>
            ) : (
              <button 
                className="resend-button"
                onClick={() => {
                  setIsEmailSent(false);
                  setCountdown(0);
                }}
              >
                Send Another Email
              </button>
            )}
            
            <button 
              className="back-button"
              onClick={handleBackToLogin}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resend-email-container">
      <div className="resend-email-card">
        <div className="email-icon">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="40" fill="#f59e0b"/>
            <path d="M25 35L40 45L55 35M25 35L40 25L55 35M25 35V55C25 57.2091 26.7909 59 29 59H51C53.2091 59 55 57.2091 55 55V35" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <h1 className="page-title">Resend Verification Email</h1>
        
        <p className="page-description">
          Didn't receive the verification email? Enter your email address below and we'll send you a new one.
        </p>
        
        <form onSubmit={handleResendEmail} className="resend-form">
          <div className="input-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className={error ? 'error' : ''}
              disabled={isLoading}
            />
            {error && <span className="error-message">{error}</span>}
          </div>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner"></div>
                Sending...
              </>
            ) : (
              'Send Verification Email'
            )}
          </button>
        </form>
        
        <div className="help-section">
          <p>Still having trouble?</p>
          <a href="mailto:support@sportagram.com" className="support-link">
            Contact Support
          </a>
        </div>
        
        <button 
          className="back-link"
          onClick={handleBackToLogin}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
};

export default ResendEmailVerification;